// frontend/src/components/asset/CorrectInspectionOdometerModal.js
/**
 * Custodian escape hatch: clear or replace a bad historical odometer reading.
 *
 * Prefer Clear when the true km for that day is unknown — inventing a guess
 * re-poisons the monotonic floor drivers must clear. Clearing keeps the
 * inspection (checklist / compliance) and removes only the km from the floor.
 *
 * Theme: Admin (red).
 */

import React, { useEffect, useState } from 'react';
import { AlertTriangle, Gauge } from 'lucide-react';
import Modal from '../UI/Modal';
import ConfirmModal from '../UI/ConfirmModal';
import PillToggle from '../UI/PillToggle';
import { useCorrectVehicleInspectionOdometer } from '../../hooks/useAssetInventoryData';
import { formatDate } from '../../utils/dateUtils';

const MODE_CLEAR = 'clear';
const MODE_SET = 'set';

const formatKm = (km) => {
  if (km == null || km === '') return null;
  const n = Number(km);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString('en-SG');
};

const CorrectInspectionOdometerModal = ({ isOpen, onClose, inspection }) => {
  const [mode, setMode] = useState(MODE_CLEAR);
  const [newKm, setNewKm] = useState('');
  const [reason, setReason] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const correctOdometer = useCorrectVehicleInspectionOdometer();

  useEffect(() => {
    if (!isOpen) return;
    setMode(MODE_CLEAR);
    setNewKm('');
    setReason('');
    setShowDiscardConfirm(false);
  }, [isOpen, inspection?.id]);

  const forceClose = () => {
    if (correctOdometer.isPending) return;
    setMode(MODE_CLEAR);
    setNewKm('');
    setReason('');
    setShowDiscardConfirm(false);
    onClose();
  };

  const isDirty = (
    mode !== MODE_CLEAR
    || String(newKm).trim() !== ''
    || reason.trim() !== ''
  );

  const requestClose = () => {
    if (correctOdometer.isPending || showDiscardConfirm) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    forceClose();
  };

  const reasonOk = reason.trim().length >= 5;
  const kmParsed = Number(String(newKm).trim());
  const kmOk = mode === MODE_CLEAR || (
    String(newKm).trim() !== ''
    && Number.isFinite(kmParsed)
    && Number.isInteger(kmParsed)
    && kmParsed >= 0
  );
  const canSubmit = reasonOk && kmOk && !correctOdometer.isPending;

  const handleSubmit = () => {
    if (!canSubmit || !inspection?.id) return;
    correctOdometer.mutate(
      {
        id: inspection.id,
        reason: reason.trim(),
        clear: mode === MODE_CLEAR,
        ...(mode === MODE_SET ? { new_km: kmParsed } : {}),
      },
      { onSuccess: forceClose },
    );
  };

  const currentKmLabel = formatKm(inspection?.odometer_km);

  return (
    <>
      <Modal
      isOpen={isOpen}
      onClose={requestClose}
      title="Correct Odometer"
      size="md"
      theme="admin"
      contentClassName="space-y-4"
      footer={
        <>
          <button
            type="button"
            onClick={requestClose}
            disabled={correctOdometer.isPending}
            className="btn btn-admin-outline"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="btn btn-admin-primary"
          >
            <Gauge className="w-4 h-4 mr-2" />
            {correctOdometer.isPending
              ? 'Saving...'
              : mode === MODE_CLEAR
                ? 'Clear Reading'
                : 'Save Correction'}
          </button>
        </>
      }
    >
      <p className="text-sm text-gray-600">
        <span className="font-medium text-gray-900">
          {inspection?.vehicle_number || 'Vehicle'}
        </span>
        {inspection?.inspection_date ? ` · ${formatDate(inspection.inspection_date)}` : ''}
        {currentKmLabel != null ? (
          <>
            {' · recorded '}
            <span className="font-semibold tabular-nums text-gray-900">
              {currentKmLabel} km
            </span>
          </>
        ) : (
          ' · no odometer on file'
        )}
      </p>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
        <p className="text-xs font-medium text-amber-900 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          Drivers cannot submit a reading below the highest km still on file.
          Clear a bad spike when the true figure for that day is unknown — do
          not invent a guess.
        </p>
      </div>

      <div>
        <p className="block text-sm font-medium text-gray-700 mb-1.5">Action</p>
        <PillToggle
          value={mode}
          onChange={setMode}
          options={[
            { id: MODE_CLEAR, label: 'Clear reading' },
            { id: MODE_SET, label: 'Set known km' },
          ]}
          variant="text"
          theme="admin"
          fullWidth
          ariaLabel="Odometer correction action"
        />
      </div>

      {mode === MODE_SET && (
        <div>
          <label
            htmlFor="correct-odo-km"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Correct odometer (km) <span className="text-red-600">*</span>
          </label>
          <input
            id="correct-odo-km"
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={newKm}
            onChange={(e) => setNewKm(e.target.value)}
            placeholder="e.g. 321500"
            className="input-admin w-full"
          />
        </div>
      )}

      <div>
        <label
          htmlFor="correct-odo-reason"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Why is this wrong? <span className="text-red-600">*</span>
        </label>
        <textarea
          id="correct-odo-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="e.g. Legacy AppSheet typo — reading jumps above later months; true km unknown"
          className="textarea-admin w-full"
        />
        <p className="text-xs text-gray-500 mt-1">
          Kept on the inspection record so the change survives audit questions.
        </p>
      </div>
      </Modal>

      <ConfirmModal
        isOpen={showDiscardConfirm}
        title="Discard odometer correction?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        theme="admin"
        onCancel={() => setShowDiscardConfirm(false)}
        onConfirm={forceClose}
      >
        <p className="text-sm text-gray-700">
          Your correction reason and replacement reading have not been saved.
        </p>
      </ConfirmModal>
    </>
  );
};

export default CorrectInspectionOdometerModal;
